import { agruparEncimadas, diaNegocio, formatoHora12, hora12, horaLocal, nombreServicios, serviciosDe, type AgendaDia, type CitaVista } from '@barbones/shared';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { cn } from '@/lib/utils';
import { aHora, aMinutos, destinoValido, horaDestino, yaInicio } from './arrastre';
import { CLASES_TARJETA, EstadoCita } from './EstadoCita';
import { EtiquetaPago } from './EtiquetaPago';
import { GrupoSinAsignar } from './GrupoSinAsignar';

/** Alto en px de cada fila del intervalo. */
const ALTO_FILA = 30;
/** Estados que dejan el hueco libre: no se dibujan ni bloquean la fila. */
const LIBERAN_HUECO = new Set<CitaVista['estado']>(['cancelada', 'no_show']);

interface Props {
  dia: string;
  apertura: string;
  cierre: string;
  intervaloMin: number;
  barberos: AgendaDia['barberos'];
  citas: CitaVista[];
  /** Muestra la columna "Sin asignar" (citas sin barbero). */
  mostrarSinAsignar?: boolean;
  onCita: (cita: CitaVista) => void;
  onHueco: (barberoId: string | null, hora: string) => void;
  /** Al soltar una cita arrastrada en otro lugar válido. Sin esta prop las citas no se arrastran. */
  onMover?: (cita: CitaVista, destino: { barberoId: string | null; hora: string }) => void;
  /** Mientras se guarda un movimiento no se puede arrastrar otra cita. */
  bloqueado?: boolean;
}

/** Agenda del día: una columna por barbero y filas por intervalo; el día actual muestra la hora en curso. */
export function VistaDia({ dia, apertura, cierre, intervaloMin, barberos, citas, mostrarSinAsignar, onCita, onHueco, onMover, bloqueado }: Props) {
  const inicioMin = aMinutos(apertura);
  // La última cita puede iniciar a la hora de salida (o de cierre), así que esa fila se incluye;
  // la rejilla se alarga lo necesario para dibujar completas las citas que terminan después.
  const ultimoInicio = Math.max(aMinutos(cierre), ...barberos.map((b) => (b.horario && !b.horario.esDescanso ? aMinutos(b.horario.salida) : 0)));
  const finCitas = Math.max(0, ...citas.filter((c) => !LIBERAN_HUECO.has(c.estado)).map((c) => aMinutos(horaLocal(new Date(c.fin)))));
  const filas = useMemo(() => {
    const lista: string[] = [];
    for (let m = inicioMin; m <= ultimoInicio || m < finCitas; m += intervaloMin) lista.push(aHora(m));
    return lista;
  }, [inicioMin, ultimoInicio, finCitas, intervaloMin]);
  const finMin = inicioMin + filas.length * intervaloMin;

  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const esHoy = diaNegocio(ahora) === dia;
  const minutoActual = aMinutos(horaLocal(ahora));

  const columnas = [
    ...barberos.map((b) => ({ id: b.id as string | null, nombre: b.nombre, horario: b.horario })),
    ...(mostrarSinAsignar ? [{ id: null, nombre: 'Sin asignar', horario: null }] : []),
  ];

  // Arrastre: la cita tomada, cuántas filas hay entre su borde superior y el punto tomado, y el destino bajo el cursor.
  const [arrastre, setArrastre] = useState<{ cita: CitaVista; filas: number } | null>(null);
  const [sobre, setSobre] = useState<{ colId: string | null; hora: string } | null>(null);
  /** El arrastre sigue vivo (dragstart sin dragend todavía). */
  const arrastrando = useRef(false);
  const diaPasado = dia < diaNegocio(ahora);
  // Las agendadas se reprograman en días por venir; las ya iniciadas se corrigen también en días pasados.
  const arrastrable = (c: CitaVista) => Boolean(onMover) && !bloqueado && (yaInicio(c) || (!diaPasado && c.estado === 'agendada'));
  const destinoDe = (col: (typeof columnas)[number], horaFila: string) => {
    if (!arrastre) return null;
    const hora = horaDestino({ horaFila, filasDesdeArriba: arrastre.filas, intervaloMin });
    const limites = { desde: filas[0] ?? apertura, hasta: filas.at(-1) ?? cierre };
    return { hora, valido: destinoValido({ columna: col, hora, cita: arrastre.cita, citas, dia, ahora: new Date(), limites, sucursal: { apertura, cierre } }) };
  };
  const terminarArrastre = () => {
    arrastrando.current = false;
    setArrastre(null);
    setSobre(null);
  };
  // Si la cita arrastrada cambia en otra pantalla (se asigna, cancela o inicia), su tarjeta se desmonta y el
  // navegador entrega el dragend a un nodo separado que React no escucha: se libera aquí.
  const citaArrastrada = arrastre?.cita;
  useEffect(() => {
    if (!citaArrastrada) return;
    const sigue = citas.some((c) => c.id === citaArrastrada.id && c.barberoId === citaArrastrada.barberoId && c.estado === citaArrastrada.estado);
    if (!sigue) {
      arrastrando.current = false;
      setArrastre(null);
      setSobre(null);
    }
  }, [citas, citaArrastrada]);
  const empezarArrastre = (e: DragEvent<HTMLElement>, c: CitaVista) => {
    const y = (e.clientY ?? 0) - e.currentTarget.getBoundingClientRect().top;
    e.dataTransfer?.setData('text/plain', c.id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    const filas = Math.max(0, Math.floor(y / ALTO_FILA));
    // Chrome cancela el arrastre si la tarjeta de origen cambia (pointer-events, opacidad) dentro del
    // mismo dragstart; el estado se aplica en la siguiente vuelta del event loop.
    arrastrando.current = true;
    setTimeout(() => arrastrando.current && setArrastre({ cita: c, filas }), 0);
  };
  /** Filas que ocuparía la cita en el destino actual, con su validez, para marcarlas. */
  const marcaDe = (col: (typeof columnas)[number], hora: string): 'valido' | 'invalido' | undefined => {
    if (!arrastre || !sobre || sobre.colId !== col.id) return undefined;
    const d = destinoDe(col, sobre.hora);
    if (!d) return undefined;
    const ini = aMinutos(d.hora);
    const dur = aMinutos(horaLocal(new Date(arrastre.cita.fin))) - aMinutos(horaLocal(new Date(arrastre.cita.inicio)));
    const m = aMinutos(hora);
    return m >= ini && m < ini + Math.max(dur, intervaloMin) ? (d.valido ? 'valido' : 'invalido') : undefined;
  };
  const alSoltar = (e: DragEvent<HTMLElement>, col: (typeof columnas)[number], horaFila: string) => {
    e.preventDefault();
    const d = destinoDe(col, horaFila);
    const c = arrastre?.cita;
    terminarArrastre();
    if (!c || !d?.valido || !onMover) return;
    if (c.barberoId === col.id && aHora(aMinutos(horaLocal(new Date(c.inicio)))) === d.hora) return;
    onMover(c, { barberoId: col.id, hora: d.hora });
  };

  /**
   * ¿Se puede agendar en esta fila para esta columna? (de la entrada a la salida inclusive, fuera de comida).
   * «Sin asignar»: dentro del horario de la barbería, aunque la rejilla se alargue por una cita que termina tarde.
   */
  const libre = (col: (typeof columnas)[number], hora: string) => {
    if (col.id === null) return aMinutos(hora) >= inicioMin && aMinutos(hora) <= aMinutos(cierre);
    const h = col.horario;
    if (!h || h.esDescanso) return false;
    const m = aMinutos(hora);
    if (m < aMinutos(h.entrada) || m > aMinutos(h.salida)) return false;
    return !(h.comidaInicio && h.comidaFin && m >= aMinutos(h.comidaInicio) && m < aMinutos(h.comidaFin));
  };

  const posicion = (c: CitaVista) => {
    const ini = aMinutos(horaLocal(new Date(c.inicio)));
    const fin = aMinutos(horaLocal(new Date(c.fin)));
    return {
      fila: Math.floor((ini - inicioMin) / intervaloMin),
      top: ((ini - inicioMin) / intervaloMin) * ALTO_FILA,
      alto: Math.max(((fin - ini) / intervaloMin) * ALTO_FILA - 3, ALTO_FILA - 3),
    };
  };

  /** Citas visibles de la columna en grupos: en «Sin asignar» se juntan las que se enciman; en un barbero, una por grupo. */
  const gruposDe = (colId: string | null) => {
    const propias = citas.filter((c) => c.barberoId === colId && !LIBERAN_HUECO.has(c.estado));
    return colId === null ? agruparEncimadas(propias) : propias.map((c) => [c]);
  };
  const propsArrastre = (c: CitaVista) => ({
    draggable: arrastrable(c),
    onDragStart: arrastrable(c) ? (e: DragEvent<HTMLButtonElement>) => empezarArrastre(e, c) : undefined,
    onDragEnd: terminarArrastre,
    className: cn(arrastrable(c) && 'cursor-grab active:cursor-grabbing', arrastre && 'pointer-events-none', arrastre?.cita.id === c.id && 'opacity-50'),
  });

  const ocupada = (colId: string | null, hora: string) => {
    const m = aMinutos(hora);
    return citas.some((c) => {
      if (c.barberoId !== colId || LIBERAN_HUECO.has(c.estado)) return false;
      const ini = aMinutos(horaLocal(new Date(c.inicio)));
      const fin = aMinutos(horaLocal(new Date(c.fin)));
      return m >= ini && m < fin;
    });
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-hoja">
      <div className="grid min-w-fit" style={{ gridTemplateColumns: `76px repeat(${columnas.length}, minmax(180px, 1fr))` }}>
        {/* Encabezados */}
        <div className="sticky left-0 top-0 z-20 border-b border-r border-border bg-surface-alt" />
        {columnas.map((col) => (
          <div key={col.id ?? 'sin'} className="border-b border-r border-border bg-surface-alt px-3 py-2 last:border-r-0">
            <p className="font-display text-[15px] leading-tight">{col.nombre}</p>
            <p className="text-[11.5px] text-foreground-muted">
              {col.id === null
                ? 'Citas por asignar'
                : !col.horario
                  ? 'Sin horario registrado'
                  : col.horario.esDescanso
                    ? 'Descanso'
                    : `${formatoHora12(col.horario.entrada)} – ${formatoHora12(col.horario.salida)}${col.horario.comidaInicio ? ` · comida ${formatoHora12(col.horario.comidaInicio)}` : ''}`}
            </p>
          </div>
        ))}

        {/* Columna de horas */}
        <div className="sticky left-0 z-10 border-r border-border bg-surface">
          {/* Cada fila lleva su hora dentro (no sobre la línea): la etiqueta coincide con el hueco de al lado. */}
          {filas.map((hora) => (
            <div
              key={hora}
              data-hora={hora}
              className={cn(
                'cifras flex items-center justify-end whitespace-nowrap border-b pr-2 text-right',
                hora.endsWith(':00') ? 'border-border/70 text-[11px] font-medium text-foreground' : 'border-border/30 text-[10.5px] text-foreground-muted/70',
              )}
              style={{ height: ALTO_FILA }}
            >
              {formatoHora12(hora)}
            </div>
          ))}
        </div>

        {columnas.map((col) => (
          <section
            key={col.id ?? 'sin'}
            aria-label={`Agenda de ${col.nombre}`}
            className="relative border-r border-border last:border-r-0"
            style={{ height: filas.length * ALTO_FILA }}
          >
            {filas.map((hora) => {
              const disponible = libre(col, hora) && !ocupada(col.id, hora);
              return (
                <div
                  key={hora}
                  data-hora={hora}
                  data-destino={marcaDe(col, hora)}
                  onDragEnter={arrastre ? () => setSobre({ colId: col.id, hora }) : undefined}
                  onDragOver={
                    arrastre
                      ? (e) => {
                          if (sobre?.colId !== col.id || sobre.hora !== hora) setSobre({ colId: col.id, hora });
                          if (destinoDe(col, hora)?.valido) e.preventDefault();
                        }
                      : undefined
                  }
                  onDrop={arrastre ? (e) => alSoltar(e, col, hora) : undefined}
                  className={cn(
                    'group relative border-b',
                    hora.endsWith(':00') ? 'border-border/70' : 'border-border/30',
                    !libre(col, hora) && 'bg-[repeating-linear-gradient(135deg,transparent_0_6px,var(--bb-muted)_6px_7px)]',
                    marcaDe(col, hora) === 'valido' && 'bg-accent-soft',
                    marcaDe(col, hora) === 'invalido' && 'bg-destructive/15',
                  )}
                  style={{ height: ALTO_FILA }}
                >
                  {marcaDe(col, hora) === 'valido' && sobre && arrastre && destinoDe(col, sobre.hora)?.hora === hora && (
                    <span data-vista-previa className="pointer-events-none absolute inset-x-1 z-[7] truncate rounded bg-accent px-2 text-[11.5px] font-medium leading-[26px] text-on-primary shadow-hoja">
                      {`${formatoHora12(hora)} · ${arrastre.cita.cliente.nombre}`}
                    </span>
                  )}
                  {disponible && (
                    <button
                      type="button"
                      onClick={() => onHueco(col.id, hora)}
                      className="flex size-full items-center gap-1 px-2 text-[11.5px] text-transparent transition-colors hover:bg-accent-soft/60 hover:text-accent focus-visible:text-accent"
                      aria-label={col.id ? `Agendar con ${col.nombre} a las ${formatoHora12(hora)}` : `Agendar sin barbero a las ${formatoHora12(hora)}`}
                    >
                      <Plus className="size-3.5" aria-hidden /> {formatoHora12(hora)}
                    </button>
                  )}
                </div>
              );
            })}

            {/* Canceladas y no-show liberan el hueco: no se dibujan para poder agendar encima. */}
            {/* En «Sin asignar» las citas pueden encimarse: las que se cruzan se dibujan como lista. */}
            {gruposDe(col.id).map((grupo) => {
                if (grupo.length > 1) {
                  const finGrupo = new Date(Math.max(...grupo.map((x) => new Date(x.fin).getTime()))).toISOString();
                  // Si empieza antes de la apertura (datos viejos), la caja se recorta a la apertura.
                  const p = posicion({ ...grupo[0]!, fin: finGrupo });
                  const recorte = p.fila < 0 ? -p.top : 0;
                  if (p.alto - recorte <= 0) return null;
                  return (
                    <GrupoSinAsignar key={grupo[0]!.id} citas={grupo} top={p.top + recorte} alto={p.alto - recorte} onCita={onCita} propsArrastre={propsArrastre} />
                  );
                }
                const c = grupo[0]!;
                const p = posicion(c);
                if (p.fila < 0) return null;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-fila={p.fila}
                    draggable={arrastrable(c)}
                    onDragStart={arrastrable(c) ? (e) => empezarArrastre(e, c) : undefined}
                    onDragEnd={terminarArrastre}
                    onClick={() => onCita(c)}
                    aria-label={`${c.cliente.nombre}, ${nombreServicios(serviciosDe(c))}, ${hora12(new Date(c.inicio))}`}
                    className={cn(
                      'absolute inset-x-1 z-[5] flex flex-col overflow-hidden rounded-md border border-border/60 border-l-[3px] px-2 py-1 text-left shadow-hoja transition-transform hover:z-10 hover:-translate-y-px',
                      CLASES_TARJETA[c.estado],
                      arrastrable(c) && 'cursor-grab active:cursor-grabbing',
                      // Mientras se arrastra, las tarjetas dejan pasar el puntero a las filas de abajo.
                      arrastre && 'pointer-events-none',
                      arrastre?.cita.id === c.id && 'opacity-50',
                    )}
                    style={{ top: p.top + 1, height: p.alto }}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-[12.5px] font-semibold">{c.cliente.nombre}</span>
                      <span className="cifras shrink-0 text-[11px] text-foreground-muted">{hora12(new Date(c.inicio))}</span>
                    </span>
                    {p.alto > 40 && (
                      <span className="truncate text-[11.5px] text-foreground-muted" title={nombreServicios(serviciosDe(c))}>
                        {nombreServicios(serviciosDe(c))}
                      </span>
                    )}
                    {p.alto > 62 ? (
                      <span className="mt-auto flex items-end justify-between gap-1">
                        <EstadoCita estado={c.estado} />
                        <EtiquetaPago metodos={c.metodosPago} />
                      </span>
                    ) : (
                      <>
                        <span className="sr-only">
                          <EstadoCita estado={c.estado} />
                        </span>
                        <EtiquetaPago metodos={c.metodosPago} compacta className="absolute bottom-0.5 right-1" />
                      </>
                    )}
                  </button>
                );
            })}

            {esHoy && minutoActual >= inicioMin && minutoActual < finMin && (
              <div
                className="pointer-events-none absolute inset-x-0 z-[6] h-0.5 bg-accent"
                style={{ top: ((minutoActual - inicioMin) / intervaloMin) * ALTO_FILA }}
                aria-hidden
              >
                <span className="absolute -left-1 -top-[3px] size-2 rounded-full bg-accent" />
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
